"use client";

import { useRef, useState, useEffect, useCallback } from "react";

export interface SlidingNavItem<K extends string = string> {
  key: K;
  label: string;
  icon?: React.ReactNode;
}

interface SlidingNavProps<K extends string = string> {
  items: SlidingNavItem<K>[];
  active: K;
  onChange: (key: K) => void;
}

export function SlidingNav<K extends string = string>({
  items,
  active,
  onChange,
}: SlidingNavProps<K>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<K, HTMLButtonElement>>(new Map());
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  const measure = useCallback(() => {
    const container = containerRef.current;
    const el = itemRefs.current.get(active);
    if (!container || !el) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    setIndicator({
      left: elRect.left - containerRect.left,
      width: elRect.width,
    });
  }, [active]);

  useEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return (
    <div
      ref={containerRef}
      className="relative flex items-center gap-0.5 rounded-lg bg-white/[0.03] p-0.5"
    >
      {/* Sliding pill */}
      <div
        className="pointer-events-none absolute top-0.5 bottom-0.5 rounded-md border border-accent/15 bg-gradient-to-br from-accent/18 to-accent/10 shadow-[0_0_12px_rgba(139,92,246,0.08),inset_0_1px_0_rgba(255,255,255,0.04)] transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ left: indicator.left, width: indicator.width }}
      />

      {items.map((item) => (
        <button
          key={item.key}
          ref={(el) => {
            if (el) itemRefs.current.set(item.key, el);
          }}
          onClick={() => onChange(item.key)}
          className={`relative z-10 flex cursor-pointer items-center gap-1.5 rounded-md px-4 py-1.5 text-[13px] font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
            active === item.key
              ? "text-accent"
              : "text-text-muted hover:text-text-secondary"
          }`}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </div>
  );
}
